import React, { useContext, useRef } from 'react';
import ReactTooltip from 'react-tooltip';
import PropTypes from 'prop-types';
import themeGet from '@styled-system/theme-get';
import styled from 'styled-components';
import i18n from '../../../../app/i18n';
import { Button } from '../../../FormStyles/Button';
import Hr from '../../../Hr';
import Text from '../../../Text';
import ResponseInput, {
  ResponseLabel,
  ResponseLabelWrapper,
} from './components/ResponseInput.jsx';
import { InteractionContext } from '../../contexts';

const QnAItem = () => {
  const tooltipRef = useRef();
  const { values, admins, handleChange, errors } = useContext(
    InteractionContext,
  );

  return (
    <>
      <StyledTooltip
        id="qna"
        place="right"
        effect="solid"
        clickable={true}
        noArrow={true}
        globalEventOff="click"
        arrowColor="transparent"
        tooltipRadius="8"
        offset={{ right: 30 }}
        ref={el => {
          tooltipRef.current = el;
        }}
      >
        <TooltipWrapper>
          <TooltipTextWrapper>
            <Text variant="body2" color="white">
              {i18n.t('tileForm:interaction.types.questionAnswer.toolTip')}
            </Text>
          </TooltipTextWrapper>
          <TooltipLinksWrapper>
            <TooltipLinks
              href="http://learning.airbo.com/en/articles/5593646-q-a-tiles"
              target="_blank"
            >
              {i18n.t(
                'tileForm:interaction.types.questionAnswer.displayPreview.userNote.linkText',
              )}
            </TooltipLinks>
            <TooltipDismiss
              onClick={() => {
                tooltipRef.current.tooltipRef = null;
                ReactTooltip.hide();
              }}
            >
              {i18n.t('tileForm:interaction.types.questionAnswer.dismiss')}
            </TooltipDismiss>
          </TooltipLinksWrapper>
        </TooltipWrapper>
      </StyledTooltip>

      <QnAButton
        data-tip
        data-for="qna"
        data-event="click"
        variant="answerBlue"
        type="button"
      >
        {i18n.t('tileForm:interaction.types.questionAnswer.buttonText.yes')}
      </QnAButton>
      <QnAButton
        data-tip
        data-for="qna"
        data-event="click"
        variant="answerBlue"
        type="button"
      >
        {i18n.t('tileForm:interaction.types.questionAnswer.buttonText.no')}
      </QnAButton>
      <Hr />
      <UserNote variant="subtitle2">
        <Infotext color="black.80">
          <InfoIcon />
          {i18n.t(
            'tileForm:interaction.types.questionAnswer.displayPreview.userNote.text',
          )}
        </Infotext>
        <Divider>|</Divider>
        <LearnMoreLink
          href="http://learning.airbo.com/en/articles/5593646-q-a-tiles"
          target="_blank"
        >
          {i18n.t(
            'tileForm:interaction.types.questionAnswer.displayPreview.userNote.linkText',
          )}
        </LearnMoreLink>
      </UserNote>
      <ResponseInput
        isDisabled
        label={i18n.t(
          'tileForm:interaction.types.questionAnswer.displayPreview.questionLabel',
        )}
        placeholder={i18n.t(
          'tileForm:interaction.types.questionAnswer.displayPreview.questionPlaceholder',
        )}
        responseText=""
        characterLimit={600}
      />
      <ResponseLabelWrapper>
        <ResponseLabel>
          {i18n.t(
            'tileForm:interaction.types.questionAnswer.displayPreview.preferredContactLabel',
          )}
        </ResponseLabel>
      </ResponseLabelWrapper>

      <PreferredContactInput
        placeholder={i18n.t(
          'tileForm:interaction.types.questionAnswer.displayPreview.preferredContactPlaceholder',
        )}
        disabled
        type="text"
      />
      <QnAButton
        variant="answerBlue"
        type="button"
        data-tip
        data-for="qna"
        data-event="click"
      >
        {i18n.t(
          'tileForm:interaction.types.questionAnswer.displayPreview.submitButtonText',
        )}
      </QnAButton>
      <QnAButton
        variant="answerBlue"
        type="button"
        data-tip
        data-for="qna"
        data-event="click"
      >
        {i18n.t(
          'tileForm:interaction.types.questionAnswer.displayPreview.cancelButtonText',
        )}
      </QnAButton>
      <Hr />
      <QnASettingsTitleWrapper>
        <QnASettingsTitle color="#545454">
          {i18n.t('tileForm:interaction.types.questionAnswer.settings.title')}
        </QnASettingsTitle>
      </QnASettingsTitleWrapper>
      <ResponseLabelWrapper>
        <ResponseLabel>
          {i18n.t('tileForm:interaction.types.questionAnswer.settings.label')}
        </ResponseLabel>
      </ResponseLabelWrapper>
      <QnASettingsSelect
        name="assigneeId"
        id="assigneeId"
        onChange={handleChange}
        value={values.assigneeId || ''}
      >
        <QnASettingsSelectOption disabled value={''}>
          {i18n.t(
            'tileForm:interaction.types.questionAnswer.settings.options.selectAdmin',
          )}
        </QnASettingsSelectOption>
        {admins.map(admin => {
          return (
            <QnASettingsSelectOption key={admin.id} value={admin.id}>
              {`${admin.name} (${admin.email})`}
            </QnASettingsSelectOption>
          );
        })}
      </QnASettingsSelect>
      {errors.assigneeId && (
        <Text as="div" margin="0 0 24px" variant="button" color="error.default">
          {errors.assigneeId}
        </Text>
      )}
    </>
  );
};

QnAItem.propTypes = {
  option: PropTypes.string,
  type: PropTypes.string,
};

export default QnAItem;

const QnASettingsSelectOption = styled.option`
  font-size: 14px;
`;

const QnASettingsSelect = styled.select`
  color: ${themeGet('colors.black.60')};
  border: 1px solid ${themeGet('colors.black.20')};
  border-radius: 8px;
  height: 40px;
  margin: 8px 0px 24px;
  font-size: 14px;
`;

const QnASettingsTitle = styled(Text)`
  font-size: 18px;
  font-weight: 500;
  margin: 0 auto 20px;
`;

const QnASettingsTitleWrapper = styled.div`
  margin: 24px 0px 16px;
`;

const PreferredContactInput = styled.input`
  height: 40px;
  margin-top: 8px;
  margin-bottom: 24px;
  font-size: 14px;
`;

const LearnMoreLink = styled.a`
  color: ${themeGet('colors.primary.100')};
`;

const Divider = styled.div`
  color: ${themeGet('colors.black.10')};
`;

const Infotext = styled(Text)`
  display: flex;
  align-items: center;
  line-height: 1.125em;
`;

const InfoIcon = styled.i`
  margin-right: 9px;
`;

InfoIcon.defaultProps = {
  className: 'fa-regular fa-circle-info',
};

const UserNote = styled(Text)`
  display: flex;
  background: ${themeGet('colors.black.5')};
  justify-content: space-around;
  -webkit-justify-content: space-evenly !important;
  margin: 14px 0px 16px;
  border: 1px solid ${themeGet('colors.black.40')};
  border-radius: 8px;
  padding: 4px 0px 4px;
`;

const QnAButton = styled(Button)`
  background: linear-gradient(
    253.58deg,
    ${themeGet('colors.primary.70')} 1.55%,
    ${themeGet('colors.primary.100')} 95.8%
  );
  padding: 10px 20px;
  font-weight: normal;
  width: 100%;
  font-size: 20px;
  line-height: 25px;
  min-height: 45px;
  margin-bottom: 16px;

  &:hover {
    background: linear-gradient(
      253.58deg,
      ${themeGet('colors.primary.70')} 1.55%,
      ${themeGet('colors.primary.80')} 95.8%
    );
    cursor: pointer;
  }
  &:active {
    box-shadow: none;
  }
`;

const TooltipWrapper = styled.div`
  line-height: 18px;
`;

const TooltipTextWrapper = styled.div`
  margin: 10px 2px 14px;
`;

const TooltipLinksWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

const TooltipLinks = styled.a`
  font-size: 14px;
  color: ${themeGet('colors.primary.30')};
`;

const TooltipDismiss = styled(TooltipLinks)`
  padding: 4px 8px;
  border: 1px solid #939393;
  border-radius: 8px;
`;

const StyledTooltip = styled(ReactTooltip)`
  border-radius: 8px !important;
  max-width: 272px;
  padding: 8px 16px !important;
`;
